import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalBody,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import NewDeleteModal from "Components/Common/NewDeleteModal";
import { toast } from "react-toastify";

import {
  addFaqQuestion,
  deleteFaqQuestion,
  fetchFaqCategoryAdmin,
  fetchFaqList,
  updateFaqQuestion,
} from "slices/faq/thunk";

import FaqQuestionForm from "./questionform";
import FaqContainer from "./faqcontainer";
import { Link, Navigate, useNavigate } from "react-router-dom";

const selectFaqState = (state: RootState) => (state as any).faq;

const faqSelector = createSelector(selectFaqState, (faq) => ({
  faqData: faq.faqData,
  loading: faq.loading,
  pagination: faq.pagination,
  categories: faq.categories,
}));

const FaqPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { faqData, loading, pagination, categories } = useSelector(faqSelector);

 const faqList = faqData?.faq || [];

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editFaqData, setEditFaqData] = useState<any>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedFaqId, setSelectedFaqId] = useState<number | null>(null);

  const loadFaqList = useCallback(() => {
    return dispatch(
      fetchFaqList({
        page,
        page_size: limit,
        category_id: 0,
        status: "",
        search: searchQuery,
      })
    );
  }, [dispatch, limit, page, searchQuery]);

  useEffect(() => {
    dispatch(fetchFaqCategoryAdmin());
  }, [dispatch]);

const navigate = useNavigate();

  useEffect(() => setPage(1), [searchQuery]);

  const handleDeleteClick = (id: number) => {
    setSelectedFaqId(id);
    setDeleteModalOpen(true);
  };
    
  useEffect(() => {
   if (isModalOpen) return;
    const handler = setTimeout(() => {
      loadFaqList();
    }, 300);

    return () => clearTimeout(handler);
  }, [isModalOpen, loadFaqList]);

  const handlePageChange = (newPage: number) => setPage(newPage);

  const validation = useFormik({
    initialValues: {
        question: "",
        answer: "",
        category_id: "",
        status: "",
        question_order: ""
    },
    validationSchema: Yup.object({
       question: Yup.string()
        .trim()
        .required("Question is required")
        .test(
          "max-words",
          "Maximum 70 words allowed",
          function (value) {
            if (!value) return true; 
            const wordCount = value.trim().split(/\s+/).length;
            return wordCount <= 70;
          }
        ),
        answer: Yup.string()
        .trim()
        .required("Answer is required")
        .test(
          "max-words",
          "Maximum 150 words allowed",
          function (value) {
            if (!value) return true;
            return value.trim().split(/\s+/).length <= 150;
          }
        ),
        category_id: Yup.string().trim().required("Category is required"),
        question_order: Yup.string().trim().required("Question order is required"),
        status:Yup.string().trim().required("Status is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
        try {
        if (isEdit && editFaqData) {
           const res =  await dispatch(updateFaqQuestion(editFaqData.id, values));
            if (!res?.success) {
              toast.error("FAQ Questions order already Exists.");
              return; 
            }
            toast.success("FAQ updated successfully!");
        } else {
          const res = await dispatch(addFaqQuestion(values));
          if (!res?.success) {
            toast.error(res?.message || "Failed to add FAQ question");
            return;
          }
          toast.success("FAQ added successfully!");
        }

        setIsModalOpen(false);
        resetForm();
        setIsEdit(false);
        setEditFaqData(null);
        } catch (err) {
        console.error("Error saving FAQ:", err);
        }
    },
    });

  const toggleModal = () => {
    if (!isModalOpen) {
      validation.resetForm();
      setIsEdit(false);
      setEditFaqData(null);
      dispatch(fetchFaqCategoryAdmin());
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleRowClick = useCallback((row: any) => {}, []);

  const handleEditClick = useCallback(
    (row: any) => {
      setIsEdit(true);
      setEditFaqData(row);
      setIsModalOpen(true);

      validation.setValues({
        question: row.question || "",
        answer: row.answer || "",
        category_id: row.category_id || 0 ,
        status: row.status || "",
        question_order: row.question_order || "" 
      });
    },
    [validation]
  );

  const filteredFaqs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return faqList;
    return faqList.filter(
      (t: any) =>
        t.question?.toLowerCase().includes(q) ||
        t.answer?.toLowerCase().includes(q)
    );
  }, [faqList, searchQuery]);

  const columns = useMemo(
    () => [
      { header: "FAQ ID", accessorKey: "id",
        cell: ({ row }: any) => {
          const id = row.original.id;
          return `FAQ${String(id).padStart(3, "0")}`;
        },
       },
      { header: "Category Name", accessorKey: "category_name" },
      { header: "Question", accessorKey: "question" },
      { header: "Answer", accessorKey: "answer" },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (cell: any) => {
            const value = cell.getValue();
            const lowerVal = value?.toString().toLowerCase();

            const statusColor = lowerVal === "active" ? "#159E3A" : "#A65F00"; // Green / Orange

            return (
            <span
                style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                color: statusColor,
                border: `1px solid ${statusColor}`,
                textTransform: "capitalize",
                }}
            >
                {value}
            </span>
            );
        },
      },
      {
        header: "Actions",
        accessorKey: "actions",
        cell: (cellProps: any) => {
          const row = cellProps.row.original;
          return (
            <ul className="list-inline hstack gap-1 mb-0">
             <li className="list-inline-item" title="Edit">
                <Button
                  color=""
                  className="btn btn-icon btn-soft-primary btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(row);
                  }}
                >
                  <i className="ri-pencil-fill"></i>
                </Button>
              </li>
              <li className="list-inline-item" title="Delete">
                <Button
                  className="btn btn-icon btn-soft-danger btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(row.id);
                  }}
                >
                  <i className="ri-delete-bin-line"></i>
                </Button>
              </li>
            </ul>
          );
        },
      },
    ],
    [handleEditClick]
  );

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="FAQs" pageTitle={["FAQs", "FAQs"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={2}>
            <div className="position-relative">
            <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={""}
                placeholder="Filter"
                disabled
            />
            <i
                className="ri-filter-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
            />
            </div>
         </Col>
          <Col md={3}>
            <div className="position-relative">
            <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={searchQuery}
                placeholder="Search Faq..."
                onChange={(e) => setSearchQuery(e.target.value)}
            />
            <i
                className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
            />
            </div>
         </Col>
          <Col md={7} className="text-end d-flex justify-content-end gap-2 align-items-center">
            <Button color="primary"  outline onClick={() => navigate('/categories')}>
              <i className="ri-stack-line align-middle me-1" />
              View Categories
            </Button>
            <Button color="primary" onClick={toggleModal}>
              + Add New Question
            </Button>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-none">
              <CardBody>
                {!loading ? (
                  filteredFaqs.length > 0 ? (
                    <FaqContainer
                      columns={columns}
                      data={filteredFaqs}
                      pagination={pagination}
                      
                      limit={limit}
                      setLimit={setLimit}
                      setPage={setPage}
                      onPageChange={handlePageChange}
                      onRowClick={handleRowClick}
                      tableClass="align-middle border-bottom rounded-pill"
                      theadClass="bg-primary text-white border-top border-success"   
                      trClass=""
                      divClass="table-responsive table-card"
                        // SearchPlaceholder="Search courses..." columns={columns}                       
                    />
                  ) : (
                    <div className="text-center p-4">No records found.</div>
                  )
                ) : (
                  <Loader />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <NewDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete FAQ"
        message="Are you sure you want to delete this FAQ?"
        onConfirm={async () => {
          if (selectedFaqId) { 
            setDeleteModalOpen(false);
            await dispatch(deleteFaqQuestion(selectedFaqId));
            toast.success("FAQ deleted successfully!");

            await loadFaqList();
          }
        }}
      />

      <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
        <ModalBody>
          <FaqQuestionForm
            validation={validation}
            isEdit={isEdit}
            categories={categories}
          />
        </ModalBody>
      </Modal>
    </div>
  );
};

export default FaqPage;
